import { createContext, useState, useEffect, ReactNode } from 'react'
import { authAPI, idpSSO } from '../services/api'
import type { User } from '../types'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  loading: boolean
  login: (token: string) => Promise<void>
  logout: () => void
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  loading: true,
  login: async () => {},
  logout: () => {},
})

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const token = localStorage.getItem('jwt_token')
    if (token) {
      // Verify token by fetching /me
      authAPI.me()
        .then((resp) => {
          setUser(resp.data)
        })
        .catch(() => {
          localStorage.removeItem('jwt_token')
        })
        .finally(() => setLoading(false))
    } else {
      // Check URL for token (SSO callback)
      const params = new URLSearchParams(window.location.search)
      const tokenParam = params.get('token')
      if (tokenParam) {
        login(tokenParam)
      } else {
        setLoading(false)
      }
    }
  }, [])

  const login = async (token: string) => {
    localStorage.setItem('jwt_token', token)
    try {
      const resp = await authAPI.me()
      setUser(resp.data)
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname)
    } catch {
      localStorage.removeItem('jwt_token')
    } finally {
      setLoading(false)
    }
  }

  const logout = () => {
    localStorage.removeItem('jwt_token')
    setUser(null)
    window.location.href = idpSSO.getLoginUrl()
  }

  return (
    <AuthContext.Provider value={{ user, isAuthenticated: !!user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}
